import { Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { PluginSlashCommandResolver } from '@gitlab-org/ai-context/node';
import {
  DynamicSlashCommandProvider,
  PLUGIN_COMMANDS_PROVIDER_PRIORITY,
  type DynamicSlashCommandsResult,
} from '../dynamic_slash_command_provider';
import { SlashCommandAction } from '../slash_command_handler';

/**
 * Surfaces slash commands contributed by installed marketplace plugins.
 * Registered with the lowest priority: built-in handlers, agent skills, and
 * user-authored custom commands all win name collisions over plugin commands.
 *
 * Commands are namespaced as `/<pluginName>:<name>` (matching how plugin agent
 * skills are namespaced) so two plugins shipping the same command name both
 * stay reachable. The bare `/<name>` is offered as an alias and goes to
 * whichever command claims it first; the namespaced name always works.
 */
@Implements(DynamicSlashCommandProvider)
@Service({
  dependencies: [PluginSlashCommandResolver],
  lifetime: ServiceLifetime.Singleton,
})
export class PluginSlashCommandProvider implements DynamicSlashCommandProvider {
  readonly priority = PLUGIN_COMMANDS_PROVIDER_PRIORITY;

  #resolver: PluginSlashCommandResolver;

  constructor(resolver: PluginSlashCommandResolver) {
    this.#resolver = resolver;
  }

  async getCommands(): Promise<DynamicSlashCommandsResult> {
    const { commands, warnings } = await this.#resolver.getPluginSlashCommands();

    return {
      commands: commands.map((command) => ({
        name: `/${command.pluginName}:${command.name}`,
        description: command.description ?? `Plugin command (${command.pluginId})`,
        action: SlashCommandAction.PluginCommand,
        aliases: [`/${command.name}`],
      })),
      warnings: warnings.map(
        (warning) => `Skipped plugin command "${warning.path}": ${warning.reason}`,
      ),
    };
  }
}
